/**
 * AdminSubNav — shared horizontal sub-navigation for all /admin/* pages.
 *
 * Why this exists:
 *   The admin section has 6+ pages (funnel, conversions, prod-readiness,
 *   stripe-status, broadcast, users). Before this component each page had
 *   its own ad-hoc set of links to *some* of the others, which meant the
 *   operator had to memorize URLs or fish through the codebase to find a
 *   page. This is the one canonical menu — drop it at the top of any
 *   admin page (next to the AdminReadinessBanner) and it just works.
 *
 * Security:
 *   Renders nothing if the user is not an admin. This is purely a UI
 *   gate; the *real* security is enforced by:
 *     - <ProtectedRoute> wrapping each route in App.js
 *     - role check in each page itself ('Navigate to=/dashboard')
 *     - _require_admin() on the backend endpoints
 *   The triple-layer gate means non-admins can never see this menu —
 *   they're redirected to /dashboard before any admin page renders.
 *
 * Highlighting:
 *   The link matching the current pathname gets a filled background and
 *   white text, making it obvious where you are.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link, useLocation } from 'react-router-dom';
import {
  Activity, AlertCircle, AlertTriangle, BarChart3, CreditCard, FileSearch, Heart, Mail, ShieldCheck, Users,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { API } from '../utils/apiBase';


const ADMIN_LINKS = [
  { to: '/admin/health',         label: 'Health',         icon: Heart,          testId: 'subnav-health' },
  { to: '/admin/activity',       label: 'Activity',       icon: Activity,       testId: 'subnav-activity' },
  { to: '/admin/errors',         label: 'Errors',         icon: AlertTriangle,  testId: 'subnav-errors' },
  { to: '/admin/incidents',      label: 'Incidents',      icon: AlertCircle,    testId: 'subnav-incidents', badgeKey: 'incidents' },
  { to: '/admin/conversions',    label: 'Conversions',    icon: BarChart3,      testId: 'subnav-conversions' },
  { to: '/admin/funnel',         label: 'Full Funnel',    icon: BarChart3,      testId: 'subnav-funnel' },
  { to: '/admin/acquisition',    label: 'Acquisition',    icon: BarChart3,      testId: 'subnav-acquisition' },
  { to: '/admin/stripe-status',  label: 'Stripe Status',  icon: CreditCard,     testId: 'subnav-stripe-status' },
  { to: '/admin/prod-readiness', label: 'Prod Readiness', icon: ShieldCheck,    testId: 'subnav-prod-readiness' },
  { to: '/admin/broadcast',      label: 'Broadcast',      icon: Mail,           testId: 'subnav-broadcast' },
  { to: '/admin/leads',          label: 'Leads',          icon: Users,          testId: 'subnav-leads' },
  { to: '/admin/users',          label: 'Users',          icon: Users,          testId: 'subnav-users' },
  { to: '/admin/resume-search',  label: 'Resume Search',  icon: FileSearch,     testId: 'subnav-resume-search' },
];


const AdminSubNav = () => {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const [openIncidents, setOpenIncidents] = useState(0);

  // Lightweight unread-badge poll. Single tiny endpoint, refreshes every
  // 60s while any admin page is open. Soft-fails to 0 if the endpoint
  // isn't reachable (auth issue, network blip, etc.) — never blocks the
  // sub-nav from rendering.
  useEffect(() => {
    if (!user || user.role !== 'admin') return undefined;
    let alive = true;
    const fetchCount = async () => {
      try {
        const r = await axios.get(`${API}/admin/incidents/count`, { withCredentials: true });
        if (alive) setOpenIncidents(r.data?.open_count || 0);
      } catch (_e) { /* noop */ }
    };
    fetchCount();
    const id = setInterval(fetchCount, 60000);
    return () => { alive = false; clearInterval(id); };
  }, [user]);

  // Hard gate: never render for non-admins. Defensive — the route guards
  // already prevent non-admins from getting here, but cheap to verify.
  if (!user || user.role !== 'admin') return null;

  return (
    <nav
      className="bg-white border-b border-[#e5e7eb] mb-6"
      role="navigation"
      aria-label="Admin section navigation"
      data-testid="admin-subnav"
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <div className="flex items-center gap-1 flex-wrap py-2 -mx-1">
          <div className="flex-shrink-0 px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] font-bold text-[#002fa7] border-r border-[#e5e7eb] mr-2">
            Admin
          </div>
          {ADMIN_LINKS.map(({ to, label, icon: Icon, testId, badgeKey }) => {
            const active = pathname === to;
            const badgeValue = badgeKey === 'incidents' && openIncidents > 0 ? openIncidents : null;
            return (
              <Link
                key={to}
                to={to}
                data-testid={testId}
                aria-current={active ? 'page' : undefined}
                className={`flex-shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-semibold transition-colors whitespace-nowrap ${
                  active
                    ? 'bg-[#0f1115] text-white'
                    : 'text-[#4b5563] hover:text-[#0f1115] hover:bg-[#f4f5f7]'
                }`}
              >
                <Icon size={14} className={active ? 'text-white' : 'text-[#6b7280]'} />
                {label}
                {badgeValue !== null && (
                  <span
                    className={`ml-1 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-extrabold ${
                      active ? 'bg-[#fbbf24] text-[#0f1115]' : 'bg-[#e11d48] text-white'
                    }`}
                    data-testid={`subnav-badge-${badgeKey}`}
                  >
                    {badgeValue > 99 ? '99+' : badgeValue}
                  </span>
                )}
              </Link>
            );
          })}
        </div>
      </div>
    </nav>
  );
};

export default AdminSubNav;
